import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Autocomplete, Pagination } from '@material-ui/lab';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineRounded from '@material-ui/icons/DeleteOutlineRounded';
import { useSelector } from 'react-redux';
import moment from 'moment';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import MemberFormDialog from './MemberFormDialog';
import RemoveMemberDialog from './RemoveMemberDialog';
import {
  addSchoolMember,
  apiErrorMessage,
  getMemberRemoval,
  getSchoolMembers,
  getSchoolSubscriptions,
  updateSchoolMember,
} from './schoolMembersApi';
import {
  INVITE_STATUS_OPTIONS,
  ROLE_FILTER_OPTIONS,
  inviteStatusLabel,
  roleLabel,
} from './memberLabels';

const PAGE_SIZE = 25;

const subscriptionLabel = ({ plan, startDate, endDate }) => {
  const range = [startDate, endDate]
    .filter(Boolean)
    .map((d) => moment(d).format('DD MMM YYYY'))
    .join(' – ');
  return `${(plan && plan.name) || 'Subscription'}${
    range ? ` (${range})` : ''
  }`;
};

const AkademySchoolMembers = () => {
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();
  const schools = useSelector(({ akademyReducer }) => akademyReducer.schools);

  const [school, setSchool] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionId, setSubscriptionId] = useState('');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [role, setRole] = useState('');
  const [licenseInviteStatus, setLicenseInviteStatus] = useState('');
  const [page, setPage] = useState(1);

  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formMember, setFormMember] = useState(undefined); // undefined closed, null add
  const [removal, setRemoval] = useState(null); // { preview, remove }
  const latestRequest = useRef(0);

  // A filter change goes back to the first page.
  const withPageReset = (setter) => (value) => {
    setPage(1);
    setter(value);
  };

  useEffect(() => {
    const timeoutId = setTimeout(
      () => withPageReset(setDebouncedSearch)(search),
      600
    );
    return () => clearTimeout(timeoutId);
  }, [search]);

  // Optional: with a subscription, each profile also reports its licence and invite.
  useEffect(() => {
    setSubscriptions([]);
    setSubscriptionId('');
    setPage(1);
    setMembers([]);
    setTotal(0);
    if (!school) return;

    getSchoolSubscriptions(school._id)
      .then(setSubscriptions)
      .catch((error) => ErrorToast(apiErrorMessage(error)));
  }, [school]);

  const loadMembers = useCallback(() => {
    if (!school) return;
    // Filters change faster than responses arrive; only the latest request may land.
    const requestId = ++latestRequest.current;
    const isLatest = () => requestId === latestRequest.current;
    setLoading(true);
    getSchoolMembers(school._id, {
      subscriptionId,
      search: debouncedSearch,
      role,
      licenseInviteStatus,
      page,
      limit: PAGE_SIZE,
    })
      .then((data) => {
        if (!isLatest()) return;
        setMembers(data.items);
        setTotal(data.total);
      })
      .catch((error) => {
        if (!isLatest()) return;
        setMembers([]);
        setTotal(0);
        ErrorToast(apiErrorMessage(error));
      })
      .finally(() => isLatest() && setLoading(false));
  }, [
    school,
    subscriptionId,
    debouncedSearch,
    role,
    licenseInviteStatus,
    page,
  ]);

  useEffect(() => loadMembers(), [loadMembers]);

  const handleSubmitForm = (payload) => {
    handleLoader(true);
    const request = formMember
      ? updateSchoolMember(school._id, formMember, payload)
      : addSchoolMember(school._id, payload);
    request
      .then((res) => {
        SuccessToast(res.message);
        setFormMember(undefined);
        loadMembers();
      })
      .catch((error) => ErrorToast(apiErrorMessage(error)))
      .finally(() => handleLoader(false));
  };

  const handleStartRemoval = (member) => {
    handleLoader(true);
    getMemberRemoval(school._id, member)
      .then(setRemoval)
      .catch((error) => ErrorToast(apiErrorMessage(error)))
      .finally(() => handleLoader(false));
  };

  const handleConfirmRemoval = () => {
    handleLoader(true);
    removal
      .remove()
      .then((res) => {
        const { licensesRevoked, invitesRevoked, quotaReturned } =
          res.data || {};
        SuccessToast(
          `${res.message}: ${licensesRevoked} licence(s) and ${invitesRevoked} invite(s) revoked, ${quotaReturned} seat(s) returned`
        );
        setRemoval(null);
        loadMembers();
      })
      .catch((error) => ErrorToast(apiErrorMessage(error)))
      .finally(() => handleLoader(false));
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Member',
        Cell: ({ row }) => {
          const { user } = row.original;
          return (
            <>
              <Typography variant="body2">
                <b>{user.name}</b>
              </Typography>
              <Typography variant="caption" display="block">
                {user.email}
              </Typography>
              {user.mobile && (
                <Typography variant="caption" display="block">
                  {user.mobile}
                </Typography>
              )}
            </>
          );
        },
      },
      {
        Header: 'Roles',
        Cell: ({ row }) => {
          const { roles, intendedRole } = row.original;
          return (
            <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
              {roles.length === 0 && <Chip size="small" label="No seat yet" />}
              {roles.map((r) => (
                <Chip
                  key={r}
                  size="small"
                  color="primary"
                  label={roleLabel(r)}
                />
              ))}
              {intendedRole && !roles.includes(intendedRole) && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Intended: ${roleLabel(intendedRole)}`}
                />
              )}
            </Box>
          );
        },
      },
      {
        Header: 'Licence',
        Cell: ({ row }) =>
          // `licenseInvite` comes back only when the list is asked for a subscription.
          row.original.profiles.map((p) => (
            <Typography key={p.profileId} variant="caption" display="block">
              {roleLabel(p.role)}
              {p.licenseInvite
                ? `: ${inviteStatusLabel(p.licenseInvite.status)}`
                : ''}
            </Typography>
          )),
      },
      {
        Header: 'Classrooms / Children',
        Cell: ({ row }) => {
          const { classrooms = [], students = [] } = row.original;
          return (
            <>
              {classrooms.map((c) => (
                <Typography key={c._id} variant="caption" display="block">
                  {c.grade ? `${c.grade.name} · ` : ''}
                  {c.sectionName}
                </Typography>
              ))}
              {students.map((s) => (
                <Typography key={s._id} variant="caption" display="block">
                  Child: {s.name}
                </Typography>
              ))}
            </>
          );
        },
      },
      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <Box display="flex">
            <Tooltip title="Edit">
              <IconButton onClick={() => setFormMember(row.original)}>
                <EditIcon color="secondary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove from school">
              <IconButton onClick={() => handleStartRemoval(row.original)}>
                <DeleteOutlineRounded color="error" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [school]
  );

  return (
    <Box style={{ width: '100%' }}>
      <Box display="flex" style={{ gap: '1rem' }}>
        <Autocomplete
          size="small"
          style={{ flex: 1 }}
          options={schools || []}
          getOptionLabel={(option) => option.name || ''}
          getOptionSelected={(option, value) => option._id === value._id}
          value={school}
          onChange={(_, value) => setSchool(value)}
          renderInput={(params) => (
            <TextField {...params} label="School" variant="outlined" />
          )}
        />
        <TextField
          size="small"
          select
          style={{ flex: 1 }}
          label="Subscription"
          variant="outlined"
          value={subscriptionId}
          onChange={(e) => {
            // The licence filter needs a subscription — the backend 400s it otherwise.
            if (!e.target.value) setLicenseInviteStatus('');
            withPageReset(setSubscriptionId)(e.target.value);
          }}
          disabled={!school || subscriptions.length === 0}
          helperText={
            school && subscriptions.length === 0
              ? 'This school has no Akademy subscription'
              : 'Optional — pick one to see licence status'
          }>
          <MenuItem value="">None</MenuItem>
          {subscriptions.map((sub) => (
            <MenuItem key={sub._id} value={sub._id}>
              {subscriptionLabel(sub)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {school && (
        <>
          <Box
            display="flex"
            alignItems="center"
            mt={2}
            style={{ gap: '1rem' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setFormMember(null)}>
              Add Member
            </Button>
            <TextField
              style={{ flex: 2 }}
              label="Search name, email or mobile"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <TextField
              select
              style={{ flex: 1 }}
              label="Role"
              variant="outlined"
              size="small"
              value={role}
              onChange={(e) => withPageReset(setRole)(e.target.value)}>
              {ROLE_FILTER_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              style={{ flex: 1 }}
              label="Licence status"
              variant="outlined"
              size="small"
              value={licenseInviteStatus}
              disabled={!subscriptionId}
              onChange={(e) =>
                withPageReset(setLicenseInviteStatus)(e.target.value)
              }>
              {INVITE_STATUS_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            {loading && <CircularProgress size="1.5rem" />}
          </Box>

          <Box my={2} style={{ width: '100%' }}>
            <Typography variant="caption" color="textSecondary">
              {total} member{total === 1 ? '' : 's'}
            </Typography>
            <BuzzTable columns={columns} data={members} />
            {total > PAGE_SIZE && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={Math.ceil(total / PAGE_SIZE)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      <MemberFormDialog
        open={formMember !== undefined}
        member={formMember}
        onClose={() => setFormMember(undefined)}
        onSubmit={handleSubmitForm}
      />
      <RemoveMemberDialog
        open={!!removal}
        preview={removal && removal.preview}
        schoolName={school && school.name}
        onClose={() => setRemoval(null)}
        onConfirm={handleConfirmRemoval}
      />
    </Box>
  );
};

export default AkademySchoolMembers;
